import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/src/services/api';
import '../v2/tokens.css';

/*
 * Public invite-acceptance page (token in URL, two-token pattern):
 * validate the emailed token, set a password, then hand off to /login.
 */

export default function InvitePage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState<
        'loading' | 'invalid' | 'form' | 'submitting' | 'done'
    >('loading');
    const [invite, setInvite] = useState<any | null>(null);
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            const response = await api
                .get(`/production-hooks/invite/validate/${token}`)
                .catch(() => null);
            if (!mounted) return;
            if (response?.data?.acceptToken) {
                setInvite(response.data);
                setState('form');
            } else {
                setState('invalid');
            }
        })();
        return () => {
            mounted = false;
        };
    }, [token]);

    const submit = async () => {
        setError(null);
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        setState('submitting');
        const response = await api
            .post('/production-hooks/invite/accept', {
                acceptToken: invite.acceptToken,
                password,
            })
            .catch(() => null);
        if (response?.data?.success) {
            setState('done');
        } else {
            setState('form');
            setError('Something went wrong — the link may have expired.');
        }
    };

    return (
        <div className="psv2 flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
            <div className="w-full max-w-[380px]">
                <div className="mb-7 flex items-center gap-3">
                    <span className="flex h-[25px] w-[25px] items-center justify-center rounded-full bg-foreground">
                        <span className="h-[8.5px] w-[8.5px] rounded-full bg-background" />
                    </span>
                    <span className="text-[13.5px] font-semibold">
                        Smilebooth Crew
                    </span>
                </div>

                {state === 'loading' && (
                    <div className="text-[13px] text-faint">Checking your invite…</div>
                )}

                {state === 'invalid' && (
                    <>
                        <h1 className="m-0 text-[20px] font-semibold tracking-[-0.02em]">
                            This invite link isn't valid
                        </h1>
                        <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                            It may have expired or already been used. Ask your
                            Smilebooth contact to send a fresh one.
                        </p>
                    </>
                )}

                {(state === 'form' || state === 'submitting') && invite && (
                    <>
                        <h1 className="m-0 text-[20px] font-semibold tracking-[-0.02em]">
                            Hi {invite.firstName} — set a password
                        </h1>
                        <p className="mt-2 text-[13px] text-muted-foreground">
                            Your login is <b>{invite.email}</b>.
                        </p>
                        <label className="mb-1.5 mt-5 block text-[12px] font-semibold text-muted-foreground">
                            Password
                        </label>
                        <input
                            autoFocus
                            type="password"
                            className="ps-input w-full text-[14px]"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <label className="mb-1.5 mt-4 block text-[12px] font-semibold text-muted-foreground">
                            Confirm password
                        </label>
                        <input
                            type="password"
                            className="ps-input w-full text-[14px]"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') submit();
                            }}
                        />
                        {error && (
                            <div className="mt-3 text-[12.5px] font-semibold text-attention">
                                {error}
                            </div>
                        )}
                        <button
                            type="button"
                            disabled={state === 'submitting'}
                            onClick={submit}
                            className="mt-5 flex h-11 w-full cursor-pointer items-center justify-center rounded-full border-0 bg-foreground font-[inherit] text-[14px] font-semibold text-background disabled:opacity-60"
                        >
                            {state === 'submitting'
                                ? 'Setting up…'
                                : 'Create my account'}
                        </button>
                    </>
                )}

                {state === 'done' && (
                    <>
                        <h1 className="m-0 text-[20px] font-semibold tracking-[-0.02em]">
                            You're set
                        </h1>
                        <p className="mt-2 text-[13px] text-muted-foreground">
                            Log in with <b>{invite?.email}</b> to see your gigs.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/login')}
                            className="mt-5 flex h-11 w-full cursor-pointer items-center justify-center rounded-full border-0 bg-foreground font-[inherit] text-[14px] font-semibold text-background"
                        >
                            Log in
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
